/**
 * The policy template library and the pure functions the generator uses: fill placeholders, check that
 * required clauses survive any tailoring, and check the Baker Tilly writing standards.
 */
import { assetsAndSoftware, dataManagement, informationSecurity, secureConfiguration } from './foundation.js';
import { accessControl, auditLogs, malwareEmailWeb, vulnerabilityManagement } from './protection.js';
import { awarenessTraining, backupRecovery, incidentResponse, serviceProviders } from './resilience.js';
import { COMMON_ASKS } from './types.js';
export * from './types.js';
export * from './practice.js';
export const POLICY_TEMPLATES_STATUS = 'draft';
/** Bumped whenever template wording changes; recorded with each generated policy. */
export const POLICY_TEMPLATES_VERSION = '2026-09-23';
export const POLICY_TEMPLATES = [
    informationSecurity,
    assetsAndSoftware,
    dataManagement,
    secureConfiguration,
    accessControl,
    vulnerabilityManagement,
    auditLogs,
    malwareEmailWeb,
    backupRecovery,
    awarenessTraining,
    serviceProviders,
    incidentResponse,
];
export const policyTemplate = (id) => POLICY_TEMPLATES.find((t) => t.id === id);
/** Templates that help meet a control, for links from the control pages. */
export const templatesForControl = (controlId) => POLICY_TEMPLATES.filter((t) => t.controls.includes(controlId));
export const questionsFor = (t) => [...COMMON_ASKS, ...t.asks];
const PLACEHOLDER = /\{\{(\w+)\}\}/g;
const capitalise = (s) => s.charAt(0).toUpperCase() + s.slice(1);
export function placeholdersIn(text) {
    return [...text.matchAll(PLACEHOLDER)].map((m) => m[1]);
}
/** Fills every {{placeholder}}; a clause that starts with a placeholder gets a capital letter. */
export function fillText(text, answers) {
    return capitalise(text.replace(PLACEHOLDER, (_, k) => (answers[k] ?? '').trim() || `[${k}]`));
}
export function fillPolicy(t, answers) {
    return {
        templateId: t.id,
        title: t.title,
        headline: t.headline,
        sections: t.sections.map((s) => ({
            heading: s.heading,
            intro: s.intro ? fillText(s.intro, answers) : undefined,
            clauses: s.clauses.map((c) => ({ ...c, text: fillText(c.text, answers) })),
        })),
    };
}
export const missingAnswers = (t, answers) => questionsFor(t).filter((k) => !answers[k]?.trim());
/**
 * Terms a required clause must keep through tailoring: every number and time period, and the obligation
 * words below when the original uses them. A tailored clause that drops one fails the check.
 */
const OBLIGATIONS = ['multi-factor', 'encrypt', 'isolated', 'immutable', 'disabled', 'removed', 'blocked', 'at least', 'automatically', 'every', 'only', 'never', 'unique', 'separate'];
export function mustKeep(text) {
    const lower = text.toLowerCase();
    const numbers = [...lower.matchAll(/\b\d+\b/g)].map((m) => m[0]);
    return [...new Set([...numbers, ...OBLIGATIONS.filter((w) => lower.includes(w))])];
}
/** Checks a generated policy against its template: every required clause present and nothing it must keep lost. */
export function checkRequired(t, generated, answers) {
    const problems = [];
    const byId = new Map(generated.sections.flatMap((s) => s.clauses).map((c) => [c.id, c.text]));
    for (const s of t.sections) {
        for (const c of s.clauses.filter((x) => x.required)) {
            const got = byId.get(c.id);
            if (!got) {
                problems.push(`required clause "${c.id}" is missing`);
                continue;
            }
            const lower = got.toLowerCase();
            const lost = mustKeep(fillText(c.text, answers)).filter((k) => !lower.includes(k));
            if (lost.length)
                problems.push(`required clause "${c.id}" lost: ${lost.join(', ')}`);
        }
    }
    return problems;
}
/** Baker Tilly writing standards checks that can be automated: no dashes, Canadian spelling, no emoji. */
const US_SPELLINGS = /\b(organization|organizations|recognize|recognized|analyze|analyzed|behavior|behaviors|center|centers|color|colors|summarize|favor|catalog|defense)\b/i;
export function writingProblems(text) {
    const problems = [];
    if (/[–—]/.test(text))
        problems.push('contains an em or en dash');
    const us = US_SPELLINGS.exec(text);
    if (us)
        problems.push(`US spelling "${us[0]}"`);
    if (/\p{Extended_Pictographic}/u.test(text))
        problems.push('contains an emoji');
    return problems;
}
const CANADIAN = {
    organization: 'organisation',
    organizations: 'organisations',
    recognize: 'recognise',
    recognized: 'recognised',
    analyze: 'analyse',
    analyzed: 'analysed',
    behavior: 'behaviour',
    behaviors: 'behaviours',
    center: 'centre',
    centers: 'centres',
    color: 'colour',
    colors: 'colours',
    summarize: 'summarise',
    favor: 'favour',
    catalog: 'catalogue',
    defense: 'defence',
};
/**
 * Fixes what writingProblems finds, where a mechanical fix keeps the meaning: a dash between numbers
 * becomes "to", any other dash a comma; US spellings become Canadian; emoji are removed. Used on the
 * policy writer's text before it is checked. People's own edits are checked, never silently changed.
 */
export function fixWriting(text) {
    const fixes = [];
    let out = text;
    if (/[–—]/.test(out)) {
        out = out.replace(/(\d)\s*[–—]\s*(\d)/g, '$1 to $2').replace(/\s*[–—]\s*/g, ', ').replace(/,\s*([,.;:])/g, '$1');
        fixes.push('replaced dashes');
    }
    out = out.replace(new RegExp(`\\b(${Object.keys(CANADIAN).join('|')})\\b`, 'gi'), (w) => {
        const ca = CANADIAN[w.toLowerCase()];
        fixes.push(`"${w}" to "${ca}"`);
        return w.charAt(0) === w.charAt(0).toUpperCase() ? ca.charAt(0).toUpperCase() + ca.slice(1) : ca;
    });
    if (/\p{Extended_Pictographic}/u.test(out)) {
        out = out.replace(/\p{Extended_Pictographic}|‍|️/gu, '').replace(/ {2,}/g, ' ').replace(/ ([,.;:])/g, '$1').trim();
        fixes.push('removed emoji');
    }
    return { text: out, fixes: [...new Set(fixes)] };
}
/** A clause may be rewritten when the template marks it for tailoring, or when it is optional. */
export const canTailor = (c) => c.tailor === true || !c.required;
export const MAX_CLAUSE_LENGTH = 2000;
/** "3.2" style numbers, as the template lays the clauses out. */
export function clauseNumbers(t) {
    return new Map(t.sections.flatMap((s, i) => s.clauses.map((c, j) => [c.id, `${i + 1}.${j + 1}`])));
}
/** The template filled with the answers, then the tailored wording over it and the omitted clauses taken out. */
export function applyTailoring(t, answers, tailoring) {
    const filled = fillPolicy(t, answers);
    if (!tailoring)
        return filled;
    const omitted = new Set(tailoring.omitted);
    return {
        ...filled,
        sections: filled.sections
            .map((s) => ({
            ...s,
            clauses: s.clauses
                .filter((c) => !(omitted.has(c.id) && !c.required))
                .map((c) => (Object.hasOwn(tailoring.clauses, c.id) ? { ...c, text: fillText(tailoring.clauses[c.id], answers) } : c)),
        }))
            .filter((s) => s.clauses.length > 0),
    };
}
const PLAIN_PROBLEM = {
    'contains an em or en dash': 'contains a long dash; use a comma, a full stop or "to" instead',
    'contains an emoji': 'contains an emoji',
};
/**
 * Everything wrong with a tailoring, in plain words the IT lead (or the writer) can act on:
 * unknown clauses, required clauses rewritten without permission or left out, empty or long text, the
 * writing standards, and last the required-clause check (numbers and obligation words kept).
 */
export function tailoringProblems(t, answers, tailoring) {
    const problems = [];
    const clauses = new Map(t.sections.flatMap((s) => s.clauses).map((c) => [c.id, c]));
    const num = clauseNumbers(t);
    for (const [id, text] of Object.entries(tailoring.clauses)) {
        const c = clauses.get(id);
        if (!c) {
            problems.push({ clauseId: null, message: `The draft names a clause that is not in this policy ("${id.slice(0, 40)}").` });
            continue;
        }
        const n = num.get(id);
        if (!canTailor(c)) {
            problems.push({ clauseId: id, message: `Clause ${n} is required and keeps the National wording, so it cannot be rewritten.` });
            continue;
        }
        if (!text.trim())
            problems.push({ clauseId: id, message: `Clause ${n} is empty.` });
        if (text.length > MAX_CLAUSE_LENGTH)
            problems.push({ clauseId: id, message: `Clause ${n} is longer than ${MAX_CLAUSE_LENGTH.toLocaleString('en-CA')} characters.` });
        for (const p of writingProblems(text))
            problems.push({ clauseId: id, message: `Clause ${n} ${(PLAIN_PROBLEM[p] ?? p.replace(/^US spelling/, 'uses the US spelling'))}.` });
    }
    for (const id of tailoring.omitted) {
        const c = clauses.get(id);
        if (!c)
            problems.push({ clauseId: null, message: `The draft leaves out a clause that is not in this policy ("${id.slice(0, 40)}").` });
        else if (c.required)
            problems.push({ clauseId: id, message: `Clause ${num.get(id)} is required and cannot be left out.` });
    }
    // The required-clause check is the final authority: nothing a required clause must keep may be lost.
    for (const p of checkRequired(t, applyTailoring(t, answers, tailoring), answers)) {
        const id = /required clause "([^"]+)"/.exec(p)?.[1] ?? null;
        const lost = /lost: (.+)$/.exec(p)?.[1];
        const n = id ? num.get(id) : '';
        problems.push({
            clauseId: id,
            message: lost ? `Clause ${n} must keep ${lost.split(', ').map((w) => `"${w}"`).join(', ')} from the National wording.` : `Clause ${n} is required and is missing.`,
        });
    }
    return problems;
}
//# sourceMappingURL=index.js.map